import { AlertCircle, CheckCircle, Clock, MessageSquare, User as UserIcon } from 'lucide-react';
import React from 'react';
import { TaskActivity } from '../types/task';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface TaskActivityFeedProps {
  activities: TaskActivity[];
  onAddComment?: (comment: string, mentions: string[]) => void;
}

const TaskActivityFeed: React.FC<TaskActivityFeedProps> = ({
  activities,
}) => {
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date().toDateString();

    // Format time as HH:MM AM/PM
    const timeStr = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    // If today, show only time
    if (date.toDateString() === today) {
      return timeStr;
    }

    // Otherwise show date and time
    const dateStr = date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    return `${dateStr} ${timeStr}`;
  };

  const getUserInitials = (user: any) => {
    if (!user) return '?';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  const renderActivityIcon = (activity: TaskActivity) => {
    if (activity.type === 'comment') {
      return <MessageSquare className="h-4 w-4 text-blue-600" />;
    } else if (activity.type === 'checklist') {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    } else {
      // Activity log
      if (activity.activity_type?.includes('checklist_item')) {
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      } else if (activity.activity_type?.includes('status')) {
        return <AlertCircle className="h-4 w-4 text-orange-600" />;
      } else if (activity.activity_type?.includes('assignment')) {
        return <UserIcon className="h-4 w-4 text-purple-600" />;
      } else {
        return <Clock className="h-4 w-4 text-gray-600" />;
      }
    }
  };

  const renderActivityDescription = (activity: TaskActivity) => {
    const userName = activity.user
      ? `${activity.user.first_name} ${activity.user.last_name}`
      : 'System';

    if (activity.type === 'comment') {
      return (
        <div>
          <div className="font-medium text-sm">{userName} commented</div>
          <div className="text-sm text-foreground mt-1 whitespace-pre-wrap bg-muted p-2 rounded">
            {activity.content}
          </div>
        </div>
      );
    } else if (activity.type === 'checklist') {
      const action = activity.activity_type?.includes('completed') ? 'completed' : 'uncompleted';
      return (
        <div className="text-sm">
          <span className="font-medium">{userName}</span> {action} checklist item:{' '}
          <span className="font-medium">{activity.content}</span>
        </div>
      );
    } else {
      // Activity log
      const fieldName = activity.field_name;
      const oldValue = activity.old_value;
      const newValue = activity.new_value;

      let description = '';
      if (activity.activity_type === 'checklist_item_completed') {
        description = `completed checklist item: ${newValue || 'Unknown'}`;
      } else if (activity.activity_type === 'checklist_item_uncompleted') {
        description = `uncompleted checklist item: ${oldValue || 'Unknown'}`;
      } else if (activity.activity_type === 'status_change') {
        description = `changed status from ${formatFieldValue(oldValue)} to ${formatFieldValue(newValue)}`;
      } else if (activity.activity_type === 'assignment_change') {
        if (!newValue || newValue === 'null') {
          description = 'unassigned the task';
        } else if (!oldValue || oldValue === 'null') {
          description = 'assigned the task';
        } else {
          description = 'reassigned the task';
        }
      } else if (activity.activity_type === 'priority_change') {
        description = `changed priority from ${formatFieldValue(oldValue)} to ${formatFieldValue(newValue)}`;
      } else {
        description = `updated ${fieldName}`;
      }

      return (
        <div className="text-sm">
          <span className="font-medium">{userName}</span> {description}
        </div>
      );
    }
  };

  const formatFieldValue = (value?: string) => {
    if (!value || value === 'null') return 'none';
    return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Group activities by date
  const groupedActivities: { [key: string]: TaskActivity[] } = {};
  activities.forEach(activity => {
    const date = new Date(activity.created_at).toDateString();
    if (!groupedActivities[date]) {
      groupedActivities[date] = [];
    }
    groupedActivities[date].push(activity);
  });

  const formatDateHeader = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No activity yet
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedActivities).map(dateStr => (
              <div key={dateStr}>
                <div className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                  {formatDateHeader(dateStr)}
                </div>
                <div className="space-y-3">
                  {groupedActivities[dateStr].map((activity) => (
                    <div key={activity.id} className="flex gap-3">
                      {/* Avatar/Icon */}
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                          {activity.user ? getUserInitials(activity.user) : renderActivityIcon(activity)}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {renderActivityDescription(activity)}
                          </div>
                          <div className="flex-shrink-0 text-xs text-muted-foreground">
                            {formatTimestamp(activity.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TaskActivityFeed;

